import { setLastPostRequest } from "../../store/slice/usageInfoSlice";
import { store } from "../../store/store";
import { DELETE, GET, PATCH, POST } from "../requestHandler";
const baseUrl = process.env.REACT_APP_BACKEND_API;

const getGroupList = async (tenantId) => {
  return await GET(`${baseUrl}/groups/${tenantId}`);
};

const getIsDuplicateGroupName = async (tenantId, groupName) => {
  return await GET(
    `${baseUrl}/duplicate/${tenantId}/${encodeURIComponent(groupName)}`,
  );
};

const getAllGroupReport = async () => {
  const tenantId = store.getState().usageInfo.tenantID;
  return await GET(`${baseUrl}/allgroupreport/${tenantId}`);
};

const getFullGroupReport = async () => {
  const tenantId = store.getState().usageInfo.tenantID;
  return await GET(`${baseUrl}/fullgroupreport/${tenantId}`, null, true, {
    timeout: 10 * 60_000,
  });
};

const getGroupData = async (tenantId, groupId, isReadOnly = false) => {
  const res = await GET(
    `${baseUrl}/latestResponse/${tenantId}/${groupId}`,
    null,
    isReadOnly,
  );

  if (
    res.status !== 200 ||
    !res.data?.hasOwnProperty("group_data") ||
    !res.data?.hasOwnProperty("readonly")
  ) {
    throw res;
  }

  return res;
};

const getGroupUsers = async (tenantId, groupId) => {
  return await GET(`${baseUrl}/groupusers/${tenantId}/${groupId}`);
};

const getGroupSupervisors = async (groupId) => {
  const tenantId = store.getState().usageInfo.tenantID;
  return await GET(`${baseUrl}/groupsupervisors/${tenantId}/${groupId}`);
};

const postGroupUsers = async (tenantId, groupId, users) => {
  return await POST(`${baseUrl}/groupusers/`, {
    tenantId,
    group_name: groupId,
    users: users,
  });
};

const postResponseHelper = async (tenantId, body, checkStatus) => {
  const res = await POST(
    `${baseUrl}/response/${tenantId}`,
    body,
    undefined,
    checkStatus,
  );

  store.dispatch(setLastPostRequest(res?.data?.timeStamp ?? null));

  return res;
};

const postResponse = async (body, checkStatus = true) => {
  const tenantId = store.getState().usageInfo.tenantID;
  return postResponseHelper(tenantId, body, checkStatus);
};

const postNewGroup = async (tenantId, data) => {
  return await POST(`${baseUrl}/newGroup/${tenantId}`, data);
};

const postRemoveGroupLock = async (groupId) => {
  const tenantId = store.getState().usageInfo.tenantID;
  return await POST(`${baseUrl}/removelock/${tenantId}`, {
    group_name: groupId,
  });
};

const postLockGroup = async (tenantId, id, checkStatus = false) => {
  return await POST(
    `${baseUrl}/lockGroup/${tenantId}`,
    { id },
    undefined,
    checkStatus,
  );
};

export const getWBSLogs = async () => {
  const tenantId = store.getState().usageInfo.tenantID;
  return await GET(`${baseUrl}/charge/all/${tenantId}`);
};

export const updateWBSLogs = async (groupId, body) => {
  const tenantId = store.getState().usageInfo.tenantID;
  return await PATCH(`${baseUrl}/charge/${tenantId}/${groupId}`, body);
};

export const deleteWBSLogs = async (groupId, body) => {
  const tenantId = store.getState().usageInfo.tenantID;
  return await DELETE(`${baseUrl}/charge/${tenantId}/${groupId}`, body);
};

/**
 * Adds charge to wbs code log
 * @param body {object}
 * @param body.groupID {string}
 * @param body.group_name {string}
 * @param body.charge_type {string}
 * @param body.number_of_onesource_companies {number}
 * @param body.number_of_uk_companies {number}
 * @param body.poa_id {string}
 * @param body.poa_start {string}
 * @param body.poa_end {string}
 * @param body.wbs_code {string}
 * @param body.selectedReturnType {string}
 * @param body.returnStatus {object}
 */
export const postCharge = async (tenantId, body) => {
  return await POST(`${baseUrl}/charge/${tenantId}`, body);
};

const postCreateGroupCopies = async (
  originalGroupID,
  originalGroupName,
  numberOfCopies,
  customNameSuffix,
) => {
  const tenantId = store.getState().usageInfo.tenantID;
  return await POST(`${baseUrl}/copy-group/${tenantId}`, {
    originalGroupID,
    originalGroupName,
    numberOfCopies,
    customNameSuffix,
  });
};

const postDeleteGroups = async (originalDocNames) => {
  const tenantId = store.getState().usageInfo.tenantID;
  return await POST(
    `${baseUrl}/delete-groups/${tenantId}`,
    { originalDocNames },
    undefined,
    true,
  );
};

const postReinstateNonSubmittedResponse = async (groupName) => {
  const tenantId = store.getState().usageInfo.tenantID;
  return await POST(
    `${baseUrl}/reinstate-nonSubmitted-response/${tenantId}`,
    { groupName },
    undefined,
    true,
  );
};

const postReinstateResponse = async (sourceGroupId, responseId) => {
  const tenantId = store.getState().usageInfo.tenantID;
  return await POST(
    `${baseUrl}/reinstate-response/${tenantId}`,
    { sourceGroupId, responseId },
    undefined,
    true,
  );
};

const getFinalisedNotSubmittedReport = async () => {
  const tenantId = store.getState().usageInfo.tenantID;
  const response = await GET(`${baseUrl}/not-submitted-report/${tenantId}`);
  return response;
};

const getProdGroups = async () => {
  const tenantId = store.getState().usageInfo.tenantID;
  return await GET(
    `${baseUrl}/copyProdGroups/${tenantId}`,
    undefined,
    undefined,
    undefined,
    true,
  );
};

const postCopyProdGroup = async (id, nameSuffix, databaseName) => {
  const tenantId = store.getState().usageInfo.tenantID;
  return await POST(
    `${baseUrl}/copyProdGroups/${tenantId}`,
    { id, nameSuffix, databaseName },
    undefined,
    true,
  );
};

const postGroupPoa = async (id, poa, poaIndex) => {
  const tenantId = store.getState().usageInfo.tenantID;
  return await POST(
    `${baseUrl}/groupPoa/${tenantId}`,
    { id, poa, poaIndex },
    undefined,
    true,
  );
};

const postReviewCalc3 = async (tenantId, body) => {
  return await POST(
    `${baseUrl}/reviewCalc3/${tenantId}`,
    body,
    undefined,
    true,
  );
};

const postReviewCalc24 = async (tenantId, body) => {
  return await POST(
    `${baseUrl}/reviewCalc24/${tenantId}`,
    body,
    undefined,
    true,
  );
};

const getTenantList = async () => {
  return await GET(`${baseUrl}/tenants`);
};

const getTenantData = async (tenantId) => {
  return await GET(`${baseUrl}/tenants/${tenantId}`);
};

const getReportingCompaniesReport = async () => {
  const tenantId = store.getState().usageInfo.tenantID;
  return await GET(
    `${baseUrl}/reportingCompaniesReport/${tenantId}`,
    undefined,
    undefined,
    true,
  );
};

export default {
  getGroupList,
  getIsDuplicateGroupName,
  getAllGroupReport,
  getFullGroupReport,
  getGroupUsers,
  getGroupData,
  postGroupUsers,
  postResponse,
  postNewGroup,
  postRemoveGroupLock,
  getGroupSupervisors,
  postCreateGroupCopies,
  postDeleteGroups,
  postReinstateNonSubmittedResponse,
  postReinstateResponse,
  postLockGroup,
  getFinalisedNotSubmittedReport,
  getProdGroups,
  postCopyProdGroup,
  postGroupPoa,
  postReviewCalc3,
  postReviewCalc24,
  getTenantList,
  getTenantData,
  getReportingCompaniesReport,
};
